import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Plus } from 'lucide-react';
import { POLICY_KEYS, POLICY_LABELS, type PolicyKey } from '@zentera/shared';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Skeleton,
  Switch,
  Table,
  TableContainer,
  TableMessage,
  TableSkeleton,
  TBody,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
  cn,
  useToast,
} from '@zentera/ui';
import { api, ApiError, fmt, FormFooter, FormSection, useForm } from '@zentera/web-core';
import { usePageTitle } from '../components/shell';
import { useCan } from '../lib/auth';

const KNOWLEDGE_NOTE = 'Este conteúdo fica somente nesta instalação e será usado pelo assistente para responder seus clientes.';

function useSaveToast() {
  const toast = useToast();
  return {
    ok: (title = 'Alterações salvas') => toast({ tone: 'success', title }),
    fail: (e: unknown) => toast({ tone: 'danger', title: 'Não foi possível salvar', description: e instanceof ApiError ? e.message : undefined }),
  };
}

/* ───────────── Dados institucionais ───────────── */

interface Institution {
  tradeName: string;
  legalName: string | null;
  supportPhone: string | null;
  whatsappNumber: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  serviceArea: string | null;
  about: string | null;
}

export function InstitutionPage() {
  usePageTitle('Dados institucionais');
  const q = useQuery({ queryKey: ['institution'], queryFn: () => api.get<Institution | null>('/api/institution') });
  return (
    <div>
      <PageHeader title="Dados institucionais" description={KNOWLEDGE_NOTE} className="mb-8" />
      {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : q.isPending ? <Skeleton className="h-96 rounded-lg" /> : <InstitutionForm initial={q.data} />}
    </div>
  );
}

function InstitutionForm({ initial }: { initial: Institution | null }) {
  const can = useCan();
  const qc = useQueryClient();
  const t = useSaveToast();
  const s = (v: string | null | undefined) => v ?? '';
  const f = useForm({
    tradeName: s(initial?.tradeName),
    legalName: s(initial?.legalName),
    supportPhone: s(initial?.supportPhone),
    whatsappNumber: s(initial?.whatsappNumber),
    email: s(initial?.email),
    website: s(initial?.website),
    address: s(initial?.address),
    serviceArea: s(initial?.serviceArea),
    about: s(initial?.about),
  });
  const save = useMutation({
    mutationFn: () => api.put('/api/institution', f.values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['institution'] });
      t.ok();
    },
    onError: f.handleError,
  });
  const v = f.values;
  return (
    <form noValidate onSubmit={(e) => { e.preventDefault(); f.clearErrors(); save.mutate(); }}>
      <fieldset disabled={!can('settings.write')}>
        <FormSection title="Empresa">
          <Field label="Nome fantasia" error={f.errors.tradeName}><Input value={v.tradeName} onChange={(e) => f.set('tradeName', e.target.value)} /></Field>
          <Field label="Razão social" optional error={f.errors.legalName}><Input value={v.legalName} onChange={(e) => f.set('legalName', e.target.value)} /></Field>
          <Field label="Sobre a empresa" optional error={f.errors.about} hint="Breve apresentação usada em respostas institucionais." className="sm:col-span-2">
            <Textarea rows={4} value={v.about} onChange={(e) => f.set('about', e.target.value)} />
          </Field>
        </FormSection>
        <FormSection title="Contato" description="Canais informados aos clientes.">
          <Field label="Telefone de suporte" optional error={f.errors.supportPhone}><Input inputMode="tel" value={v.supportPhone} onChange={(e) => f.set('supportPhone', e.target.value)} /></Field>
          <Field label="WhatsApp" optional error={f.errors.whatsappNumber}><Input inputMode="tel" value={v.whatsappNumber} onChange={(e) => f.set('whatsappNumber', e.target.value)} /></Field>
          <Field label="E-mail" optional error={f.errors.email}><Input type="email" value={v.email} onChange={(e) => f.set('email', e.target.value)} /></Field>
          <Field label="Site" optional error={f.errors.website}><Input placeholder="https://" value={v.website} onChange={(e) => f.set('website', e.target.value)} /></Field>
        </FormSection>
        <FormSection title="Localização" description="Endereço de atendimento presencial e cidades atendidas.">
          <Field label="Endereço" optional error={f.errors.address} className="sm:col-span-2"><Input value={v.address} onChange={(e) => f.set('address', e.target.value)} /></Field>
          <Field label="Área de cobertura" optional error={f.errors.serviceArea} hint="Cidades e bairros atendidos." className="sm:col-span-2">
            <Textarea rows={3} value={v.serviceArea} onChange={(e) => f.set('serviceArea', e.target.value)} />
          </Field>
        </FormSection>
      </fieldset>
      {can('settings.write') && (
        <FormFooter error={save.error}>
          <Button type="submit" variant="primary" loading={save.isPending}>Salvar</Button>
        </FormFooter>
      )}
    </form>
  );
}

/* ───────────── Planos ───────────── */

interface Plan {
  id: string;
  name: string;
  downloadMbps: number;
  uploadMbps: number;
  monthlyPriceCents: number;
  installationFeeCents: number;
  loyaltyMonths: number;
  description: string | null;
  active: boolean;
}

export function PlansPage() {
  usePageTitle('Planos');
  const can = useCan();
  const qc = useQueryClient();
  const t = useSaveToast();
  const [editing, setEditing] = useState<Plan | 'new' | null>(null);
  const q = useQuery({ queryKey: ['plans'], queryFn: () => api.get<{ items: Plan[] }>('/api/plans') });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/plans/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['plans'] }); t.ok('Plano removido'); },
    onError: t.fail,
  });
  return (
    <div className="space-y-5">
      <PageHeader
        title="Planos"
        description="Planos comercializados. Somente planos ativos são oferecidos pelo assistente."
        actions={can('knowledge.write') && <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>Novo plano</Button>}
      />
      <TableContainer>
        <Table>
          <THead>
            <tr><Th>Plano</Th><Th align="right">Velocidade</Th><Th align="right">Mensalidade</Th><Th align="right">Instalação</Th><Th align="right">Fidelidade</Th><Th>Situação</Th><Th className="w-10"><span className="sr-only">Ações</span></Th></tr>
          </THead>
          <TBody>
            {q.isPending ? <TableSkeleton columns={7} rows={3} /> : q.isError ? (
              <TableMessage columns={7}><ErrorState error={q.error} onRetry={() => void q.refetch()} /></TableMessage>
            ) : q.data.items.length === 0 ? (
              <TableMessage columns={7}>
                <EmptyState title="Nenhum plano cadastrado" description="Cadastre os planos para que o assistente possa informar valores e velocidades." action={can('knowledge.write') && <Button variant="primary" onClick={() => setEditing('new')}>Cadastrar plano</Button>} />
              </TableMessage>
            ) : (
              q.data.items.map((p) => (
                <Tr key={p.id} className={cn(!p.active && 'text-fg-3')}>
                  <Td>
                    <div className="font-medium">{p.name}</div>
                    {p.description && <div className="max-w-sm truncate text-xs text-fg-3">{p.description}</div>}
                  </Td>
                  <Td align="right">{p.downloadMbps} / {p.uploadMbps} Mbps</Td>
                  <Td align="right" className="font-medium">{fmt.brlCents(p.monthlyPriceCents)}</Td>
                  <Td align="right">{p.installationFeeCents ? fmt.brlCents(p.installationFeeCents) : 'Grátis'}</Td>
                  <Td align="right">{p.loyaltyMonths ? `${p.loyaltyMonths} meses` : '—'}</Td>
                  <Td>{p.active ? <Badge tone="success">Ativo</Badge> : <Badge tone="muted">Inativo</Badge>}</Td>
                  <Td>
                    {can('knowledge.write') && (
                      <Menu>
                        <MenuTrigger asChild><IconButton size="sm" label={`Ações de ${p.name}`} icon={<MoreHorizontal />} /></MenuTrigger>
                        <MenuContent>
                          <MenuItem onSelect={() => setEditing(p)}>Editar</MenuItem>
                          <MenuItem danger onSelect={() => remove.mutate(p.id)}>Remover</MenuItem>
                        </MenuContent>
                      </Menu>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </TableContainer>
      {editing && <PlanDialog plan={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

const centsToInput = (c: number) => (c / 100).toFixed(2).replace('.', ',');
const inputToCents = (v: string) => Math.round(Number(v.replace(/\./g, '').replace(',', '.')) * 100);

function PlanDialog({ plan, onClose }: { plan: Plan | null; onClose: () => void }) {
  const qc = useQueryClient();
  const t = useSaveToast();
  const [name, setName] = useState(plan?.name ?? '');
  const [down, setDown] = useState(String(plan?.downloadMbps ?? ''));
  const [up, setUp] = useState(String(plan?.uploadMbps ?? ''));
  const [price, setPrice] = useState(plan ? centsToInput(plan.monthlyPriceCents) : '');
  const [fee, setFee] = useState(plan ? centsToInput(plan.installationFeeCents) : '0,00');
  const [loyalty, setLoyalty] = useState(String(plan?.loyaltyMonths ?? 0));
  const [description, setDescription] = useState(plan?.description ?? '');
  const [active, setActive] = useState(plan?.active ?? true);
  const save = useMutation({
    mutationFn: () => {
      const body = { name, downloadMbps: Number(down), uploadMbps: Number(up), monthlyPriceCents: inputToCents(price), installationFeeCents: inputToCents(fee || '0'), loyaltyMonths: Number(loyalty || 0), description, active };
      return plan ? api.put(`/api/plans/${plan.id}`, body) : api.post('/api/plans', body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['plans'] }); t.ok(plan ? 'Plano atualizado' : 'Plano cadastrado'); onClose(); },
  });
  const e = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={plan ? 'Editar plano' : 'Novo plano'}
        footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Salvar</Button></>}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome" error={e.name} className="sm:col-span-2"><Input value={name} onChange={(ev) => setName(ev.target.value)} autoFocus /></Field>
          <Field label="Download (Mbps)" error={e.downloadMbps}><Input inputMode="numeric" value={down} onChange={(ev) => setDown(ev.target.value.replace(/\D/g, ''))} /></Field>
          <Field label="Upload (Mbps)" error={e.uploadMbps}><Input inputMode="numeric" value={up} onChange={(ev) => setUp(ev.target.value.replace(/\D/g, ''))} /></Field>
          <Field label="Mensalidade (R$)" error={e.monthlyPriceCents}><Input inputMode="decimal" value={price} onChange={(ev) => setPrice(ev.target.value)} /></Field>
          <Field label="Taxa de instalação (R$)" error={e.installationFeeCents}><Input inputMode="decimal" value={fee} onChange={(ev) => setFee(ev.target.value)} /></Field>
          <Field label="Fidelidade (meses)" error={e.loyaltyMonths}><Input inputMode="numeric" value={loyalty} onChange={(ev) => setLoyalty(ev.target.value.replace(/\D/g, ''))} /></Field>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-fg-2">Situação</span>
            <div className="flex h-8 items-center gap-2"><Switch label="Plano ativo" checked={active} onCheckedChange={setActive} /><span className="text-sm text-fg-2">{active ? 'Ativo' : 'Inativo'}</span></div>
          </div>
          <Field label="Descrição" optional error={e.description} className="sm:col-span-2"><Textarea rows={3} value={description} onChange={(ev) => setDescription(ev.target.value)} placeholder="Benefícios inclusos, Wi-Fi, roteador…" /></Field>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────── Horários ───────────── */

const WEEKDAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
type Day = { weekday: number; closed: boolean; opens: string; closes: string };

export function HoursPage() {
  usePageTitle('Horários');
  const q = useQuery({ queryKey: ['hours'], queryFn: () => api.get<{ days: Day[]; note: string | null }>('/api/business-hours') });
  return (
    <div>
      <PageHeader title="Horário de atendimento humano" description="Fora deste horário o assistente informa quando a equipe volta e registra a solicitação." className="mb-8" />
      {q.isError ? <ErrorState error={q.error} /> : q.isPending ? <Skeleton className="h-80 rounded-lg" /> : <HoursForm key={JSON.stringify(q.data)} initial={q.data} />}
    </div>
  );
}

function HoursForm({ initial }: { initial: { days: Day[]; note: string | null } }) {
  const can = useCan();
  const qc = useQueryClient();
  const t = useSaveToast();
  const [days, setDays] = useState(initial.days);
  const [note, setNote] = useState(initial.note ?? '');
  const save = useMutation({
    mutationFn: () => api.put('/api/business-hours', { days, note }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['hours'] }); t.ok(); },
  });
  const errors = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const set = (i: number, patch: Partial<Day>) => setDays((d) => d.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const order = [1, 2, 3, 4, 5, 6, 0];
  const editable = can('settings.write');
  return (
    <div className="max-w-2xl space-y-6">
      <ul className="divide-y divide-line rounded-lg border border-line">
        {order.map((wd) => {
          const i = days.findIndex((d) => d.weekday === wd);
          const d = days[i]!;
          const err = errors[`days.${i}.closes`];
          return (
            <li key={wd} className="flex flex-wrap items-center gap-4 px-4 py-3">
              <span className="w-24 text-sm font-medium">{WEEKDAYS[wd]}</span>
              <label className="flex items-center gap-2 text-sm text-fg-2">
                <Switch label={`Aberto ${WEEKDAYS[wd]}`} checked={!d.closed} disabled={!editable} onCheckedChange={(open) => set(i, { closed: !open })} />
                {d.closed ? 'Fechado' : 'Aberto'}
              </label>
              {!d.closed && (
                <div className="flex items-center gap-2 text-sm">
                  <Input type="time" aria-label={`Abertura ${WEEKDAYS[wd]}`} className="w-28" disabled={!editable} value={d.opens} onChange={(e) => set(i, { opens: e.target.value })} />
                  <span className="text-fg-3">até</span>
                  <Input type="time" aria-label={`Fechamento ${WEEKDAYS[wd]}`} className="w-28" disabled={!editable} value={d.closes} aria-invalid={!!err} onChange={(e) => set(i, { closes: e.target.value })} />
                </div>
              )}
              {err && <span className="text-xs text-danger">{err}</span>}
            </li>
          );
        })}
      </ul>
      <Field label="Observação" optional hint="Ex.: feriados nacionais sem atendimento presencial; plantão técnico 24h.">
        <Textarea rows={2} disabled={!editable} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {editable && <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Salvar horários</Button>}
    </div>
  );
}

/* ───────────── Políticas ───────────── */

export function PoliciesPage() {
  usePageTitle('Políticas');
  const q = useQuery({ queryKey: ['policies'], queryFn: () => api.get<{ items: { key: PolicyKey; content: string; updatedAt: string | null }[] }>('/api/policies') });
  return (
    <div className="space-y-8">
      <PageHeader title="Políticas" description="Regras que o assistente segue ao responder sobre contratação, cobrança, instalação, cancelamento e suporte." />
      {q.isError ? <ErrorState error={q.error} /> : q.isPending ? <Skeleton className="h-96 rounded-lg" /> : (
        <div className="space-y-6">
          {POLICY_KEYS.map((k) => {
            const p = q.data.items.find((x) => x.key === k)!;
            return <PolicyEditor key={`${k}:${p.updatedAt}`} policyKey={k} initial={p.content} updatedAt={p.updatedAt} />;
          })}
        </div>
      )}
    </div>
  );
}

function PolicyEditor({ policyKey, initial, updatedAt }: { policyKey: PolicyKey; initial: string; updatedAt: string | null }) {
  const can = useCan();
  const qc = useQueryClient();
  const t = useSaveToast();
  const [content, setContent] = useState(initial);
  const save = useMutation({
    mutationFn: () => api.put(`/api/policies/${policyKey}`, { content }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['policies'] }); t.ok(`${POLICY_LABELS[policyKey].title} salva`); },
    onError: t.fail,
  });
  const meta = POLICY_LABELS[policyKey];
  return (
    <section className="grid gap-x-10 gap-y-3 border-b border-line pb-6 last:border-b-0 md:grid-cols-[240px_minmax(0,1fr)]">
      <div>
        <h2 className="text-sm font-medium">{meta.title}</h2>
        <p className="mt-1 text-xs leading-relaxed text-fg-3">{meta.hint}</p>
        <p className="mt-2 text-2xs text-fg-4">{updatedAt ? `Atualizada ${fmt.relative(updatedAt)}` : 'Não preenchida'}</p>
      </div>
      <div className="space-y-2">
        <Textarea rows={5} aria-label={meta.title} disabled={!can('knowledge.write')} value={content} onChange={(e) => setContent(e.target.value)} />
        {can('knowledge.write') && (
          <div className="flex justify-end">
            <Button size="sm" disabled={content === initial} loading={save.isPending} onClick={() => save.mutate()}>Salvar</Button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ───────────── FAQ ───────────── */

interface Faq { id: string; question: string; answer: string; category: string | null; published: boolean; updatedAt: string }

export function FaqPage() {
  usePageTitle('Perguntas frequentes');
  const can = useCan();
  const qc = useQueryClient();
  const t = useSaveToast();
  const [editing, setEditing] = useState<Faq | 'new' | null>(null);
  const q = useQuery({ queryKey: ['faq'], queryFn: () => api.get<{ items: Faq[] }>('/api/faq') });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/faq/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['faq'] }); t.ok('Pergunta removida'); },
    onError: t.fail,
  });
  return (
    <div className="space-y-5">
      <PageHeader
        title="Perguntas frequentes"
        description="Respostas oficiais usadas pelo assistente. Itens não publicados ficam guardados sem uso."
        actions={can('knowledge.write') && <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>Nova pergunta</Button>}
      />
      <TableContainer>
        <Table>
          <THead><tr><Th>Pergunta</Th><Th>Categoria</Th><Th>Situação</Th><Th>Atualizada</Th><Th className="w-10"><span className="sr-only">Ações</span></Th></tr></THead>
          <TBody>
            {q.isPending ? <TableSkeleton columns={5} rows={4} /> : q.isError ? (
              <TableMessage columns={5}><ErrorState error={q.error} /></TableMessage>
            ) : q.data.items.length === 0 ? (
              <TableMessage columns={5}><EmptyState title="Nenhuma pergunta cadastrada" description="Comece pelas dúvidas que mais chegam ao atendimento: segunda via, prazos de instalação, mudança de endereço." action={can('knowledge.write') && <Button variant="primary" onClick={() => setEditing('new')}>Cadastrar pergunta</Button>} /></TableMessage>
            ) : (
              q.data.items.map((f) => (
                <Tr key={f.id} onClick={can('knowledge.write') ? () => setEditing(f) : undefined}>
                  <Td className="max-w-xl whitespace-normal">
                    <div className="font-medium">{f.question}</div>
                    <div className="line-clamp-1 text-xs text-fg-3">{f.answer}</div>
                  </Td>
                  <Td className="text-fg-2">{f.category ?? '—'}</Td>
                  <Td>{f.published ? <Badge tone="success">Publicada</Badge> : <Badge tone="muted">Rascunho</Badge>}</Td>
                  <Td className="text-fg-2">{fmt.relative(f.updatedAt)}</Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    {can('knowledge.write') && (
                      <Menu>
                        <MenuTrigger asChild><IconButton size="sm" label="Ações" icon={<MoreHorizontal />} /></MenuTrigger>
                        <MenuContent>
                          <MenuItem onSelect={() => setEditing(f)}>Editar</MenuItem>
                          <MenuItem danger onSelect={() => remove.mutate(f.id)}>Remover</MenuItem>
                        </MenuContent>
                      </Menu>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </TableContainer>
      {editing && <FaqDialog faq={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function FaqDialog({ faq, onClose }: { faq: Faq | null; onClose: () => void }) {
  const qc = useQueryClient();
  const t = useSaveToast();
  const [question, setQuestion] = useState(faq?.question ?? '');
  const [answer, setAnswer] = useState(faq?.answer ?? '');
  const [category, setCategory] = useState(faq?.category ?? '');
  const [published, setPublished] = useState(faq?.published ?? true);
  const save = useMutation({
    mutationFn: () => {
      const body = { question, answer, category, published };
      return faq ? api.put(`/api/faq/${faq.id}`, body) : api.post('/api/faq', body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['faq'] }); t.ok(); onClose(); },
  });
  const e = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        width="lg"
        title={faq ? 'Editar pergunta' : 'Nova pergunta'}
        footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Salvar</Button></>}
      >
        <div className="space-y-4">
          <Field label="Pergunta" error={e.question}><Input value={question} onChange={(ev) => setQuestion(ev.target.value)} autoFocus /></Field>
          <Field label="Resposta" error={e.answer}><Textarea rows={6} value={answer} onChange={(ev) => setAnswer(ev.target.value)} /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Categoria" optional error={e.category}><Input placeholder="Financeiro, Instalação…" value={category} onChange={(ev) => setCategory(ev.target.value)} /></Field>
            <label className="flex items-center gap-2 self-end pb-1.5 text-sm text-fg-2">
              <Checkbox checked={published} onCheckedChange={setPublished} /> Publicar para o assistente
            </label>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
